import { isAppRoute } from "@/seo/routeManifest";

/**
 * Client-side page-view recording for the public marketing pages.
 *
 * Tag Manager counts one page view when a document loads. Our marketing
 * pages do not load a document per click — React swaps the content — so
 * without this every page after the one a visitor lands on would go
 * uncounted, and the dashboard would claim nobody ever reaches /pricing.
 *
 * This is also the single place the rule "analytics never sees the app
 * surface" is written down in the client, and the one place to test it.
 * It is the second of two defences, not the only one: public → app
 * transitions are full document loads (`components/navigation/AppEntryLink`),
 * so the container is not merely quiet on /session — it is absent.
 */

/** Custom event name, so a GA4 tag fires on ours and not on history. */
export const PAGE_VIEW_EVENT = "revetra.page_view";

/**
 * Pushes a page view unless the route is part of the app surface.
 *
 * Pushing to `dataLayer` when no container ever loads is inert — the
 * array simply grows and nothing reads it — so this needs no knowledge
 * of whether analytics is configured or opted out. Keeping it ignorant
 * of the container ID is deliberate: there is exactly one switch, in the
 * head, and this is not a second one that could disagree with it.
 */
export function recordPageView(pathname: string, title: string): void {
  if (typeof window === "undefined") return;
  if (isAppRoute(pathname)) return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({
    event: PAGE_VIEW_EVENT,
    // GA4's page_view wants a full URL in page_location. It is supplied
    // here rather than left to Tag Manager's built-in Page URL variable,
    // which is captured at container load and goes stale the moment the
    // router changes the path without a document load.
    page_location: `${window.location.origin}${pathname}`,
    page_path: pathname,
    page_title: title,
  });
}
